import FormatCrypto from "@hl/base-components/lib/FormatCrypto";
import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import {
  OUTLINE_COLOR,
  PRIMARY_COLOR,
  SECONDARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import {
  useDisabledMarketplaceFF,
  useExternalMarketplaceUrl,
} from "@hl/shared-features/lib/features/marketplace/hooks/useMarketplaces";
import BidModalHighlight from "@hl/shared-features/lib/features/marketplace/modals/BidModal";
import BuyModalHighlight from "@hl/shared-features/lib/features/marketplace/modals/BuyModal";
import ListModalHighlight from "@hl/shared-features/lib/features/marketplace/modals/ListModal";
import ManageListingsModal, {
  ManageListingsModalTitle,
} from "@hl/shared-features/lib/features/marketplace/modals/ManageListingsModal";
import ManageOffersModal, {
  ManageOffersModalTitle,
} from "@hl/shared-features/lib/features/marketplace/modals/ManageOffersModal";
import { MarketplaceSelect } from "@hl/shared-features/lib/features/mint/MarketplaceSelect";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import {
  Button,
  Flex,
  Grid,
  Loader,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useListings } from "@reservoir0x/reservoir-kit-ui";

import TransferButton from "~features/MintPage/transfer/TransferButton";
import useMarketplaceState from "~features/token-details/hook/useMarketplaceState";
import useMintState from "~hooks/useMintState";

import { SeriesToken } from "../../apollo/graphql.generated";
import MintCardButton, {
  MintButtonPosition,
} from "../MintPage/MintVector/MintCardButton";

export type ListingHighlight = ReturnType<typeof useListings>["data"][0];

type Props = {
  token: Omit<SeriesToken, "attributes">;
};

const TokenDetailsCardSection1155 = ({ token }: Props) => {
  const {
    mintVector,
    handleMint,
    address,
    chainId,
    collection,
    walletAddress,
    isMarketplaceEnabledForCollectionChain,
    isImported,
    marketplaceId,
    totalSoldCount,
  } = useMintState();
  const { tokenId, userBalance, id, imageUrl, animationUrl, name, minted } =
    token;
  const {
    listings,
    isLoadingListings,
    refetchListings,
    ownerListings,
    refetchOwnerListings,
    ownerBids,
    isLoadingOwnerBids,
    refetchOwnerBids,
    collectionMarketData,
    refetchToken,
    tokenOwnerProfile,
    refetchBids,
  } = useMarketplaceState();

  const isLoading = isLoadingOwnerBids || isLoadingListings;
  const highestBid = ownerBids[0];
  const floor = collectionMarketData?.floorAsk?.price?.amount?.native ?? 0;
  const bidsAboveFloor = ownerBids.filter(
    (bid) => parseFloat(bid.price?.amount?.native ?? 0) > floor
  );
  const isOwner = !!userBalance;
  const disabledMarketplace = useDisabledMarketplaceFF();
  const marketplaceUrl = useExternalMarketplaceUrl(collection, tokenId);

  const theme = useMantineTheme();
  const { isExtraSmall } = useMantineMediaQueries({ withInitialValues: true });
  const { pushModal } = useModalStack();
  const listingToBuy = listings.find(
    (l) =>
      l.status === "active" &&
      l.maker.toLowerCase() !== walletAddress?.toLowerCase()
  );
  const offerToAccept = ownerBids[0];
  const canBuy = !!listingToBuy;
  const canBid =
    !userBalance || (!!userBalance && userBalance < totalSoldCount);
  const canCancel = ownerListings.length > 0;
  const canSell = !!userBalance && !!offerToAccept;

  const onMintClick = (fromChain: number) =>
    handleMint(fromChain, id, imageUrl || "", animationUrl || "");
  if ((!mintVector && !isImported) || !collection) {
    return null;
  }
  const listing = listings?.[0] as ListingHighlight;
  const listingPrice = listing?.price;
  const hasBids = ownerBids.length > 0;
  const hasBidsOrLoading = isLoadingOwnerBids || hasBids;

  if (isLoading) {
    return (
      <Flex py={10} w="100%" justify="center">
        <Loader />
      </Flex>
    );
  }
  if (disabledMarketplace) {
    if (!marketplaceUrl) {
      return null;
    }
    return (
      <Button
        size="lg"
        w="100%"
        component="a"
        href={marketplaceUrl ?? ""}
        target="_blank"
      >
        View on marketplace
      </Button>
    );
  }

  if (!isMarketplaceEnabledForCollectionChain || !collection || !tokenId) {
    if (
      collection &&
      tokenId &&
      minted &&
      !!userBalance &&
      !isMarketplaceEnabledForCollectionChain
    ) {
      return (
        <TransferButton
          onTransferComplete={() => refetchToken?.()}
          token={token}
          account={tokenOwnerProfile}
          listingPrice={listingPrice}
        />
      );
    }
    return null;
  }

  const buySmallSize = canBid || isOwner || canSell;
  const buyCols = buySmallSize && !isExtraSmall && canBid && !isOwner ? 6 : 12;
  const bidSmallSize = canBuy || isOwner || canSell;
  const bidCols = bidSmallSize && !isExtraSmall && canBuy && !isOwner ? 6 : 12;

  if (minted) {
    return (
      <Grid w="100%" gutter={8}>
        {canBuy && (
          <Grid.Col span={buyCols}>
            <Button
              w="100%"
              px="xs"
              size="lg"
              onClick={() =>
                pushModal(
                  <BuyModalHighlight
                    tokenId={tokenId!}
                    src={imageUrl || ""}
                    collectionData={collection}
                    orderId={listingToBuy.id}
                    onCompleted={() => {
                      refetchListings();
                      refetchToken?.();
                    }}
                  />,
                  {
                    size: "lg",
                    title: (
                      <Text size="md" fw={WEIGHT_BOLD}>
                        Complete purchase
                      </Text>
                    ),
                  }
                )
              }
            >
              <Text
                color={TEXT_COLOR.INVERTED_PRIMARY}
                size="sm"
                fw={WEIGHT_BOLD}
                sx={{ textOverflow: "ellipsis", overflow: "hidden" }}
              >
                Buy ·{" "}
                <FormatCrypto
                  amount={listing.price?.amount?.raw}
                  symbol={listing.price?.currency?.symbol}
                  onlyText
                />
              </Text>
            </Button>
          </Grid.Col>
        )}
        {canBid && (
          <Grid.Col span={bidCols}>
            <Button
              w="100%"
              px="xs"
              size="lg"
              color={SECONDARY_COLOR}
              sx={{ border: `1px solid ${theme.colors.divider[0]}` }}
              onClick={() =>
                pushModal(
                  <BidModalHighlight
                    tokenId={tokenId}
                    src={imageUrl || ""}
                    collectionData={collection}
                    onCompleted={() => {
                      refetchToken?.();
                      refetchBids();
                    }}
                  />,
                  {
                    size: "lg",
                    title: (
                      <Text size="md" fw={WEIGHT_BOLD}>
                        Make an offer
                      </Text>
                    ),
                  }
                )
              }
            >
              <Text
                size="sm"
                sx={{ textOverflow: "ellipsis", overflow: "hidden" }}
                fw={WEIGHT_BOLD}
              >
                Make offer
              </Text>
            </Button>
          </Grid.Col>
        )}
        {isOwner && (
          <Grid.Col xs={canSell && !isExtraSmall ? 6 : 12}>
            {!canCancel ? (
              <Button
                size="lg"
                w="100%"
                px="xs"
                color={canBuy ? OUTLINE_COLOR : PRIMARY_COLOR}
                disabled={isLoadingListings}
                onClick={() =>
                  pushModal(
                    <ListModalHighlight
                      src={imageUrl || ""}
                      collectionData={collection}
                      tokenId={tokenId || ""}
                      onCompleted={() => refetchOwnerListings()}
                    />,
                    {
                      size: "lg",
                      title: (
                        <Text size="md" fw={WEIGHT_BOLD}>
                          Create listing
                        </Text>
                      ),
                    }
                  )
                }
              >
                List for sale
              </Button>
            ) : (
              <Button
                size="lg"
                color={canBuy ? OUTLINE_COLOR : PRIMARY_COLOR}
                w="100%"
                px={8}
                onClick={() =>
                  pushModal(
                    <ManageListingsModal
                      collectionData={{
                        id: collection?.id,
                        onchainId: collection?.onchainId,
                        address: collection?.address || "",
                        chainId: collection?.chainId,
                        name: collection?.name || "",
                        marketplaceId: marketplaceId,
                        standard: collection?.standard,
                      }}
                      tokenId={tokenId ?? ""}
                      onCompleted={() => {
                        refetchOwnerListings();
                        refetchListings();
                        refetchToken?.();
                      }}
                    />,
                    {
                      size: "100%",
                      title: (
                        <ManageListingsModalTitle
                          tokenName={token.name}
                          collectionName={collection?.name}
                        />
                      ),
                    }
                  )
                }
              >
                Manage listings
              </Button>
            )}
          </Grid.Col>
        )}
        {canSell && hasBids && (
          <Grid.Col xs={isOwner && !isExtraSmall ? 6 : 12}>
            <Button
              size="lg"
              px="xs"
              color={OUTLINE_COLOR}
              w="100%"
              onClick={() =>
                pushModal(
                  <ManageOffersModal
                    collectionData={{
                      id: collection?.id,
                      onchainId: collection?.onchainId,
                      address: collection?.address || "",
                      chainId: collection?.chainId,
                      name: collection?.name || "",
                      marketplaceId: marketplaceId,
                      standard: collection.standard,
                    }}
                    tokenId={tokenId ?? ""}
                    onCompleted={() => {
                      refetchOwnerBids();
                      refetchBids();
                      refetchToken?.();
                    }}
                  />,
                  {
                    size: "100%",
                    title: (
                      <ManageOffersModalTitle
                        tokenName={name}
                        collectionName={collection?.name}
                      />
                    ),
                  }
                )
              }
            >
              Manage offers
            </Button>
          </Grid.Col>
        )}
        {canSell && hasBidsOrLoading && (
          <Grid.Col>
            <Stack spacing={0} pb={8} justify="space-between" w="100%">
              {hasBids && (
                <>
                  <Text fw={WEIGHT_BOLD} size="sm">
                    You have {ownerBids.length} offer
                    {ownerBids.length === 1 ? "" : "s"} for this item
                  </Text>
                  <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                    The highest offer is{" "}
                    <FormatCrypto
                      onlyText
                      amount={highestBid?.price?.amount?.raw}
                      symbol={highestBid?.price?.currency?.symbol}
                    />
                    <br />
                    {bidsAboveFloor?.length} offer
                    {bidsAboveFloor?.length === 1 ? " is" : "s are"} higher than
                    the floor price
                  </Text>
                </>
              )}
              {isLoadingOwnerBids && <Loader />}
            </Stack>
          </Grid.Col>
        )}
      </Grid>
    );
  }

  if (minted && tokenId) {
    return (
      <MarketplaceSelect
        address={address}
        tokenId={tokenId ?? ""}
        chainId={chainId}
        buttonProps={{
          size: "xl",
          fullWidth: true,
        }}
        collectionName={collection?.name ?? ""}
        collectionId={collection?.id ?? ""}
      />
    );
  }

  return (
    <MintCardButton
      showErrorIcon={false}
      onMintClick={onMintClick}
      showMintFee
      showGateRequirementsModal
      size="xl"
      fullWidth
      tokenId={id}
      disabled={!imageUrl ?? false}
      buttonPosition={MintButtonPosition.TokenDetails}
      enableCrossChainMint={collection.flagVariations.enableCrossChainMinting}
    />
  );
};

export default TokenDetailsCardSection1155;
